import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import { FormDialog, Grid, SvgIcon, SvgIconButton, Tooltip, Typography } from '@kis-technologies/kis-ui';
import RebootIcon from 'assets/rebootIcon.jpg';
import ReloadIcon from 'assets/reloadIcon.jpg';
import KioskPreview from 'assets/previewIcon.png';
import ScheduleIcon from 'assets/scheduleIcon.jpg';
import { useActions } from 'hooks/useActions';
import { KIOSK_URL } from 'services/api/base';
import { selectors as authSelectors } from 'store/auth';
import { actions as kioskActions, selectors as kioskSelectors } from 'store/kiosks';
import RestockOverlay from './RestockOverlay';
import ScheduleOverlay from './ScheduleOverlay';
import { useStyles } from '../styled';
import { KioskActionsProps } from '../types';

const KioskActions = (props: KioskActionsProps) => {
  const { kiosk, onEdit, onDelete } = props;

  const { rebootKiosk, createKioskLoginToken, resetLoginToken, getKioskSchedule } = useActions({
    rebootKiosk: kioskActions.rebootKiosk,
    createKioskLoginToken: kioskActions.createKioskLoginToken,
    resetLoginToken: kioskActions.resetLoginToken,
    getKioskSchedule: kioskActions.getKioskSchedule
  });

  const admin = useSelector(authSelectors.selectAdmin);
  const loginToken = useSelector(kioskSelectors.selectKioskLoginToken);

  const isGlobalAdmin = admin?.role === 'GlobalAdmin';
  const isReadOnlyAdmin = admin?.role === 'ReadOnlyAdmin';

  const previewKiosk = get(kiosk, 'configuration.kioskPreview.enabled');

  const [showKioskSchedule, setShowKioskSchedule] = useState(false);
  const [showRebootConfirmation, setShowRebootConfirmation] = useState(false);
  const [showTicketStockOverlay, setShowTicketStockOverlay] = useState(false);

  const classes = useStyles()();

  useEffect(() => {
    if (kiosk && loginToken && loginToken[kiosk.id]) {
      window.open(`${KIOSK_URL}?id=${btoa(kiosk.id)}&key=${loginToken[kiosk.id]}`);
      resetLoginToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginToken]);

  const onButtonClick = (value: string) => {
    if (value === 'schedule') {
      getKioskSchedule({ kioskId: kiosk.id });
      setShowKioskSchedule(true);
    } else if (value === 'reboot') {
      setShowRebootConfirmation(true);
    } else {
      setShowTicketStockOverlay(true);
    }
  };

  const onScheduleClose = () => {
    setShowKioskSchedule(false);
  };

  const onRebootKioskConfirmClick = () => {
    setShowRebootConfirmation(false);
    rebootKiosk({ kioskId: kiosk.id, adminId: admin ? admin.id : '' });
  };

  const handlePreviewClick = () => {
    if (kiosk) {
      createKioskLoginToken({ kioskId: kiosk.id, adminId: admin ? admin.id : '' });
    }
  };

  return (
    <>
      {showRebootConfirmation && (
        <FormDialog
          cancelButtonAction={() => setShowRebootConfirmation(false)}
          cancelButtonText="Cancel"
          confirmButtonAction={onRebootKioskConfirmClick}
          confirmButtonText="Reboot"
          open={showRebootConfirmation}
        >
          <Typography fontSize={24} maxWidth="524px">
            Are you sure you want to reboot {kiosk.name}?
          </Typography>
        </FormDialog>
      )}
      <Grid className={classes.KioskActionsButtons}>
        {!isReadOnlyAdmin && (
          <>
            <Tooltip title="Reload Ticket Stock" arrow placement="top" verticalOffset={-8}>
              <img
                src={ReloadIcon}
                alt="Reload Icon"
                onClick={() => onButtonClick('reload')}
                data-testid="kiosk-restock-button"
              />
            </Tooltip>
            <Tooltip title="Kiosk Schedule" arrow placement="top" verticalOffset={-8}>
              <img
                src={ScheduleIcon}
                alt="Schedule Icon"
                onClick={() => onButtonClick('schedule')}
                data-testid="kiosk-schedule-button"
              />
            </Tooltip>
            <Tooltip title="Reboot Kiosk" arrow placement="top" verticalOffset={-8}>
              <img
                src={RebootIcon}
                alt="Reboot Icon"
                style={kiosk.online ? { opacity: 1 } : { opacity: 0.5, cursor: 'not-allowed' }}
                onClick={() => kiosk.online && onButtonClick('reboot')}
                data-testid="kiosk-reboot-button"
              />
            </Tooltip>
          </>
        )}
        <Tooltip title="Edit Kiosk" arrow placement="top" verticalOffset={-8}>
          <SvgIconButton onClick={onEdit}>
            <SvgIcon name="edit_circle" size={38} />
          </SvgIconButton>
        </Tooltip>
        {isGlobalAdmin && (
          <Tooltip title="Delete Kiosk" arrow placement="top" verticalOffset={-8}>
            <SvgIconButton onClick={() => onDelete(kiosk)}>
              <SvgIcon name="exit_circle" size={38} />
            </SvgIconButton>
          </Tooltip>
        )}
        {previewKiosk && (
          <Tooltip title="Preview Kiosk" arrow placement="top" verticalOffset={-8}>
            <img
              src={KioskPreview}
              alt="Preview Icon"
              onClick={() => handlePreviewClick()}
              data-testid="kiosk-preview-button"
            />
          </Tooltip>
        )}
      </Grid>

      {showKioskSchedule && <ScheduleOverlay onClose={onScheduleClose} isOpen={showKioskSchedule} kiosk={kiosk} />}

      {showTicketStockOverlay && (
        <RestockOverlay
          isOpen={showTicketStockOverlay}
          onClose={() => setShowTicketStockOverlay(false)}
          kiosk={kiosk}
        />
      )}
    </>
  );
};

export default KioskActions;
